// Global tracking of pending cart mutations (quantity changes, additions, removals)
if (typeof window !== 'undefined' && !window._cartFetchInterceptorInstalled) {
  window._cartFetchInterceptorInstalled = true;
  window._cartMutationsInFlight = [];

  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
    const isCartMutation = url.includes('/cart/change') || url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/clear');

    const fetchPromise = originalFetch.apply(this, args);

    if (isCartMutation) {
      window._cartMutationsInFlight.push(fetchPromise);
      fetchPromise.finally(() => {
        const idx = window._cartMutationsInFlight.indexOf(fetchPromise);
        if (idx !== -1) {
          window._cartMutationsInFlight.splice(idx, 1);
        }
      });
    }

    return fetchPromise;
  };
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartData = event?.cartData || event?.cart;
        if (cartData && typeof cartData.item_count !== 'undefined') {
          this.updateProgressBar(cartData.item_count);
          this.updateFromCartPayload(cartData);
        }
      });
    }
  }

  connectedCallback() {
    this.setupCheckoutHandler();
  }

  setupCheckoutHandler() {
    const checkoutBtn = this.querySelector('#CartDrawer-Checkout') || this.querySelector('.cart-drawer__checkout-btn');
    const form = this.querySelector('#CartDrawer-Form') || document.getElementById('CartDrawer-Form');
    if (!checkoutBtn || !form) return;

    if (checkoutBtn._hasCheckoutHandler) return;
    checkoutBtn._hasCheckoutHandler = true;

    checkoutBtn.addEventListener('click', async (event) => {
      // Ensure all pending quantity adjustments (+ / -) resolve completely before executing form submission
      const hasPendingFetches = window._cartMutationsInFlight && window._cartMutationsInFlight.length > 0;
      const hasActiveSpinners = !!this.querySelector('cart-drawer-items .loading__spinner:not(.hidden), .loading-overlay:not(.hidden)');

      if (hasPendingFetches || hasActiveSpinners) {
        event.preventDefault();
        event.stopImmediatePropagation();

        checkoutBtn.setAttribute('disabled', 'disabled');
        checkoutBtn.classList.add('loading');

        try {
          if (window._cartMutationsInFlight && window._cartMutationsInFlight.length > 0) {
            await Promise.allSettled(window._cartMutationsInFlight);
          }
        } catch (e) {
          console.error('Cart mutation wait failed:', e);
        }

        // Allow microtask queue and section HTML re-render to paint
        await new Promise((resolve) => setTimeout(resolve, 150));

        checkoutBtn.removeAttribute('disabled');
        checkoutBtn.classList.remove('loading');

        // Submit native form to checkout
        this.submitToCheckout(form);
      }
    });
  }

  submitToCheckout(form) {
    let checkoutInput = form.querySelector('input[name="checkout"]');
    if (!checkoutInput) {
      checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '1';
      form.appendChild(checkoutInput);
    }
    form.submit();
  }

  updateFromCartPayload(cart) {
    if (!cart) return;

    const itemCount = typeof cart.item_count !== 'undefined' ? cart.item_count : 0;

    // Read applied discounts directly from Shopify's native cart payload
    const totalDiscount = cart.total_discount || 0;
    const discountRow = this.querySelector('.cart-drawer__breakdown-row--discount');
    const discountLabel = this.querySelector('.cart-drawer__discount-label');
    const discountAmount = this.querySelector('.cart-drawer__discount-amount');

    if (totalDiscount > 0) {
      let discountTitle = 'Automatic Discount';
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        discountTitle = cart.cart_level_discount_applications[0].title;
      } else if (cart.discount_applications && cart.discount_applications.length > 0) {
        discountTitle = cart.discount_applications[0].title;
      } else if (itemCount >= 4) {
        discountTitle = '15% OFF';
      } else if (itemCount === 3) {
        discountTitle = '10% OFF';
      }

      if (discountRow) discountRow.style.display = 'flex';
      if (discountLabel) discountLabel.textContent = discountTitle;
      if (discountAmount) {
        discountAmount.textContent = '-' + this.formatMoney(totalDiscount);
        discountAmount.style.color = '#2D5A27';
      }
    } else if (discountRow) {
      discountRow.style.display = 'none';
    }

    // Shipping row update
    const breakdownRows = Array.from(this.querySelectorAll('.cart-drawer__breakdown-row'));
    const shippingRow = breakdownRows.find(r => r.querySelector('.cart-drawer__breakdown-label')?.textContent.trim() === 'Shipping');
    if (shippingRow) {
      const valEl = shippingRow.querySelector('.cart-drawer__breakdown-val');
      if (valEl) {
        if (itemCount >= 2) {
          valEl.innerHTML = '<span class="cart-drawer__free-pill">FREE</span>';
        } else if (itemCount === 1) {
          valEl.textContent = '$5.00';
        } else {
          valEl.textContent = '$0.00';
        }
      }
    }

    // Line items strikethrough prices: original_line_price (crossed out) vs final_line_price (active price)
    if (cart.items && cart.items.length) {
      cart.items.forEach((item, index) => {
        const itemRow = this.querySelector(`#CartDrawer-Item-${index + 1}`);
        if (!itemRow) return;

        const oldPriceEl = itemRow.querySelector('.cart-item__old-price');
        const finalPriceEl = itemRow.querySelector('.cart-item__final-price') || itemRow.querySelector('.price--final');

        if (item.original_line_price > item.final_line_price) {
          if (oldPriceEl) {
            oldPriceEl.textContent = this.formatMoney(item.original_line_price);
            oldPriceEl.style.display = 'inline';
          }
          if (finalPriceEl) {
            finalPriceEl.textContent = this.formatMoney(item.final_line_price);
          }
        } else {
          if (oldPriceEl) {
            oldPriceEl.style.display = 'none';
          }
          if (finalPriceEl) {
            finalPriceEl.textContent = this.formatMoney(item.final_line_price);
          }
        }
      });
    }

    // Update total USD
    const totalVal = this.querySelector('.cart-drawer__total-val');
    if (totalVal) {
      const shippingCents = itemCount === 1 ? 500 : 0;
      totalVal.textContent = this.formatMoney((cart.total_price || 0) + shippingCents);
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  updateProgressBar(itemCount) {
    if (typeof itemCount === 'undefined' || isNaN(itemCount)) {
      const rewards = this.querySelector('.cart-drawer__rewards');
      if (rewards && rewards.dataset.cartCount) {
        itemCount = parseInt(rewards.dataset.cartCount, 10);
      } else {
        return;
      }
    }

    const fill = this.querySelector('.cart-drawer__progress-fill');
    const milestone1 = this.querySelector('.cart-drawer__milestone.milestone-1');
    const milestone2 = this.querySelector('.cart-drawer__milestone.milestone-2');
    const milestone3 = this.querySelector('.cart-drawer__milestone.milestone-3');
    const rewardsMsg = this.querySelector('.cart-drawer__rewards-msg');

    let width = 0;
    let msg = '';
    let isUnlocked = false;

    if (itemCount >= 4) {
      width = 100;
      msg = '✓ Maximum Tier Unlocked: 15% OFF + Free Shipping!';
      isUnlocked = true;
    } else if (itemCount === 3) {
      width = 68;
      msg = '✓ 10% OFF + Free Shipping Unlocked! Add 1 more cup for 15% OFF';
      isUnlocked = true;
    } else if (itemCount === 2) {
      width = 35;
      msg = '✓ Free Shipping Unlocked! Add 1 more cup for 10% OFF';
      isUnlocked = true;
    } else if (itemCount === 1) {
      width = 18;
      msg = 'Add 1 more cup to unlock Free Shipping ($5.00 shipping on 1 cup)';
      isUnlocked = false;
    } else {
      width = 0;
      msg = 'Add 2 cups to unlock Free Shipping';
      isUnlocked = false;
    }

    if (fill) {
      fill.style.width = width + '%';
    }

    if (rewardsMsg) {
      rewardsMsg.textContent = msg;
      rewardsMsg.classList.toggle('is-unlocked', isUnlocked);
    }

    if (milestone1) milestone1.classList.toggle('is-reached', itemCount >= 2);
    if (milestone2) milestone2.classList.toggle('is-reached', itemCount >= 3);
    if (milestone3) milestone3.classList.toggle('is-reached', itemCount >= 4);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;

    // Ensure is-empty class is removed if line items exist in drawer
    const hasItems = this.querySelectorAll('.cart-item').length > 0;
    if (hasItems) {
      this.classList.remove('is-empty');
      this.querySelector('.drawer__inner')?.classList.remove('is-empty');
      this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    }

    this.updateProgressBar();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, shouldOpen = true) {
    if (!parsedState) return;

    // Detect if cart has items from parsedState or section HTML
    let itemCount = typeof parsedState.item_count !== 'undefined' ? parsedState.item_count : null;
    let isDrawerEmpty = false;

    if (parsedState.sections && parsedState.sections['cart-drawer']) {
      const parsedDoc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
      const parsedDrawer = parsedDoc.querySelector('cart-drawer');
      if (parsedDrawer) {
        isDrawerEmpty = parsedDrawer.classList.contains('is-empty');
      }
      if (itemCount === null) {
        const rewardsEl = parsedDoc.querySelector('.cart-drawer__rewards');
        if (rewardsEl && rewardsEl.getAttribute('data-cart-count')) {
          itemCount = parseInt(rewardsEl.getAttribute('data-cart-count'), 10);
        } else if (parsedDoc.querySelectorAll('.cart-item').length > 0) {
          itemCount = parsedDoc.querySelectorAll('.cart-item').length;
        } else {
          itemCount = isDrawerEmpty ? 0 : 1;
        }
      }
    } else if (itemCount !== null) {
      isDrawerEmpty = itemCount === 0;
    }

    if (itemCount === null) itemCount = 0;

    // CRITICAL: Manage is-empty across cart-drawer root, drawer__inner, and cart-drawer-items
    if (itemCount > 0 || !isDrawerEmpty) {
      this.classList.remove('is-empty');
      const inner = this.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      const drawerItems = this.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.classList.remove('is-empty');
    } else {
      this.classList.add('is-empty');
      const inner = this.querySelector('.drawer__inner');
      if (inner) inner.classList.add('is-empty');
      const drawerItems = this.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.classList.add('is-empty');
    }

    this.productId = parsedState.id;
    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !parsedState.sections[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    }

    // Re-verify after innerHTML replacement so new child elements don't retain is-empty if items present
    if (itemCount > 0 || !isDrawerEmpty) {
      this.classList.remove('is-empty');
      const inner = this.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      const drawerItems = this.querySelector('cart-drawer-items');
      if (drawerItems) drawerItems.classList.remove('is-empty');
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.updateProgressBar(itemCount);
      this.setupCheckoutHandler();
      this.updateFromCartPayload(parsedState);
      if (shouldOpen) {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : html;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

window.updateCartDrawerTierProgress = function(itemCount) {
  const drawer = document.querySelector('cart-drawer');
  if (drawer && typeof drawer.updateProgressBar === 'function') {
    drawer.updateProgressBar(itemCount);
  }
};
